import { useState } from 'react'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { Button, Field, IconButton, Input, Select, Switch } from '@/components/common'
import { nextPlayerPropColumn } from './playerPropBoardModel'
import { PropColumnFields } from './PlayerPropBoardEditor'
import { discoveryNote, leagueChoices, leagueKey, linesDiscoveryNote, useParlayBuilderLines, usePlayerPropMarkets } from './playerPropDiscovery'
import {
  BUILDER_EVENT_LIMITS, BUILDER_LIMITS, PARLAY_BUILDER_TEMPLATES, builderAuthoringIssues, builderTemplateFor, groupFromTemplate,
  groupFromPostedMarkets, groupSuggestionsFromMarkets, leagueFor, lineChoices, nextGameLine,
} from './parlayBuilderModel'
import styles from './nativeSportsbook.module.css'
import { isLiveSource } from './catalogSources'

const TEMPLATE_LEAGUES = PARLAY_BUILDER_TEMPLATES.map(({ sport, competition }) => ({ sport, competition }))
const columnsKey = (columns) => columns.map((column) => `${column.criterion_id}:${column.selection}:${column.line_millis ?? ''}`).join(',')

/** Drops optional keys the schema forbids when empty, as setPath does for top-level fields. */
function cleanLeague(league) {
  const next = { ...league }
  if (!next.milestones) delete next.milestones
  if (!next.game_lines?.length) delete next.game_lines
  return next
}

/** A column patch, with a blank line meaning each player's main line. */
function patchedColumn(column, patch) {
  const next = { ...column, ...patch }
  if (next.selection === 'participant' || next.line_millis === '') delete next.line_millis
  return next
}

function GroupEditor({ group, position, total, markets, starters, disabled, onChange, onRemove }) {
  const columns = group.columns || []
  const setColumns = (next) => onChange({ ...group, columns: next })
  const update = (index, patch) => setColumns(columns.map((column, at) => (at === index ? patchedColumn(column, patch) : column)))
  const apply = (key) => {
    const starter = starters.find((candidate) => candidate.key === key)
    if (!starter) return
    const fresh = groupFromTemplate(starter)
    onChange({ ...group, label: fresh.label, header: fresh.header, columns: fresh.columns })
  }
  const next = nextPlayerPropColumn(columns, markets)
  const add = () => {
    const label = next.selection === 'yes' && next.line_millis != null ? `${next.line_millis / 1000}+` : next.label
    setColumns([...columns, { id: globalThis.crypto.randomUUID(), ...next, label: label.slice(0, 12) }])
  }
  return (
    <div className={styles.builderGroup} data-testid="parlay-builder-group">
      <div className={styles.playerPropColumnHeader}>
        <Field label={`Stat ${position + 1}`}><Input size="compact" value={group.label} maxLength={16} disabled={disabled} onChange={(event) => onChange({ ...group, label: event.target.value })} /></Field>
        <IconButton variant="danger" className={styles.rowIcon} label={`Remove ${group.label || 'stat'}`} disabled={disabled || total <= 1} onClick={onRemove}><Trash2 size={14} /></IconButton>
      </div>
      <Field label="Grid header"><Input size="compact" value={group.header} maxLength={24} disabled={disabled} onChange={(event) => onChange({ ...group, header: event.target.value })} /></Field>
      <Field label="Start from" help="Replaces this stat with a provider template or a ladder built from posted markets.">
        <Select size="compact" value="" disabled={disabled} onChange={(event) => apply(event.target.value)}>
          <option value="">Choose a ladder…</option>
          {starters.map((starter) => <option key={starter.key} value={starter.key}>{starter.name || starter.label}</option>)}
        </Select>
      </Field>
      {columns.map((column, index) => (
        <div key={column.id} className={styles.playerPropColumn} data-testid="parlay-builder-column">
          <div className={styles.playerPropColumnHeader}>
            <Field label={`Column ${index + 1}`}><Input size="compact" value={column.label} maxLength={12} disabled={disabled} onChange={(event) => update(index, { label: event.target.value })} /></Field>
            <IconButton variant="danger" className={styles.rowIcon} label={`Remove column ${column.label}`} disabled={disabled || columns.length <= 1} onClick={() => setColumns(columns.filter((_, at) => at !== index))}><Trash2 size={14} /></IconButton>
          </div>
          <PropColumnFields column={column} markets={markets} onUpdate={(patch) => update(index, patch)} />
        </div>
      ))}
      {/* A new column needs a posted market to name, so it waits for discovery. */}
      <Button size="compact" variant="secondary" disabled={disabled || columns.length >= BUILDER_LIMITS.columns || !next} onClick={add}><Plus size={14} aria-hidden="true" /> Add column</Button>
    </div>
  )
}

function GameLinesEditor({ league, choices, note, disabled, onChange }) {
  const lines = league.game_lines || []
  const update = (index, patch) => onChange(lines.map((line, at) => (at === index ? { ...line, ...patch } : line)))
  const next = nextGameLine(lines, choices)
  return (
    <div className={styles.builderSection}>
      <span className={styles.builderSectionTitle}>Game lines</span>
      {lines.map((line, index) => {
        const known = choices.some((choice) => choice.criterion_id === line.criterion_id)
        return (
          <div key={line.id} className={styles.playerPropColumn} data-testid="parlay-builder-line">
            <div className={styles.playerPropColumnHeader}>
              <Field label={`Tab ${index + 1}`}><Input size="compact" value={line.label} maxLength={16} disabled={disabled} onChange={(event) => update(index, { label: event.target.value })} /></Field>
              <IconButton variant="danger" className={styles.rowIcon} label={`Remove ${line.label || 'game line'}`} disabled={disabled} onClick={() => onChange(lines.filter((_, at) => at !== index))}><Trash2 size={14} /></IconButton>
            </div>
            <Field label="Line market">
              <Select size="compact" value={line.criterion_id} disabled={disabled} onChange={(event) => update(index, { criterion_id: event.target.value })}>
                {!known ? <option value={line.criterion_id}>Provider market {line.criterion_id}</option> : null}
                {choices.map((choice) => <option key={choice.criterion_id} value={choice.criterion_id} disabled={choice.criterion_id !== line.criterion_id && lines.some((other) => other.criterion_id === choice.criterion_id)}>{choice.label}</option>)}
              </Select>
            </Field>
          </div>
        )
      })}
      <Button size="compact" variant="secondary" disabled={disabled || lines.length >= BUILDER_LIMITS.lines || !next} onClick={() => onChange([...lines, { id: globalThis.crypto.randomUUID(), ...next }])}><Plus size={14} aria-hidden="true" /> Add game line</Button>
      {note ? <p className={styles.inspectorHint} role="note">{note}</p> : null}
    </div>
  )
}

function LeagueEditor({ league, position, total, choices, used, hub, catalog, slug, disabled, issues, onChange, onMove, onRemove }) {
  const live = isLiveSource(catalog?.source)
  const discovery = usePlayerPropMarkets({ slug, catalog, sport: league.sport, competition: league.competition })
  const lineDiscovery = useParlayBuilderLines({ slug, catalog, sport: league.sport, competition: league.competition })
  const markets = live && discovery.data?.state === 'ready' ? discovery.data.markets : []
  const templateGroups = builderTemplateFor(league.sport, league.competition)?.milestones?.groups || []
  const starters = [...templateGroups.map((group) => ({ ...group, name: `${group.label} (provider template)` })), ...groupSuggestionsFromMarkets(markets, templateGroups)]
  const lines = lineChoices(live && lineDiscovery.data?.state === 'ready' ? lineDiscovery.data.lines : [], league.sport, league.competition)
  const groups = league.milestones?.groups || []
  const update = (patch) => onChange(cleanLeague({ ...league, ...patch }))
  const setGroups = (next) => update({ milestones: { ...league.milestones, groups: next } })
  const chooseLeague = (value) => {
    const choice = choices.find((candidate) => leagueKey(candidate.sport, candidate.competition) === value)
    if (choice) onChange(leagueFor(choice.sport, choice.competition, undefined, league.id))
  }
  const unusedStarter = starters.find((starter) => !groups.some((group) => columnsKey(group.columns || []) === columnsKey(starter.columns)))
  // Without a ladder, a stat can still start from any posted column (an over/under pick, one yes line).
  const shownColumns = groups.flatMap((group) => group.columns || [])
  const canStartPosted = Boolean(nextPlayerPropColumn([], markets))
  const canAddPosted = Boolean(nextPlayerPropColumn(shownColumns, markets))
  const note = discoveryNote(live, discovery)
  return (
    <div className={styles.builderLeague} data-testid="parlay-builder-league">
      <div className={styles.playerPropColumnHeader}>
        <Field label="League" help={hub ? "A builder on a league hub uses the hub's league." : undefined}>
          {hub
            ? <Input size="compact" readOnly value={league.competition} />
            : <Select size="compact" value={leagueKey(league.sport, league.competition)} disabled={disabled} onChange={(event) => chooseLeague(event.target.value)}>
              {choices.map((choice) => {
                const key = leagueKey(choice.sport, choice.competition)
                return <option key={key} value={key} disabled={key !== leagueKey(league.sport, league.competition) && used.has(key.toLowerCase())}>{choice.competition}</option>
              })}
            </Select>}
        </Field>
        <IconButton className={styles.rowIcon} label={`Move ${league.label} earlier`} disabled={disabled || position === 0} onClick={() => onMove(-1)}><ArrowUp size={14} /></IconButton>
        <IconButton className={styles.rowIcon} label={`Move ${league.label} later`} disabled={disabled || position === total - 1} onClick={() => onMove(1)}><ArrowDown size={14} /></IconButton>
        <IconButton variant="danger" className={styles.rowIcon} label={`Remove ${league.label}`} disabled={disabled || total <= 1} onClick={onRemove}><Trash2 size={14} /></IconButton>
      </div>
      <div className={styles.playerPropColumnPair}>
        <Field label="Chip label"><Input size="compact" value={league.label} maxLength={12} disabled={disabled} onChange={(event) => update({ label: event.target.value })} /></Field>
        <Field label="Games listed">
          <Select size="compact" value={String(league.event_limit)} disabled={disabled} onChange={(event) => update({ event_limit: Number.parseInt(event.target.value, 10) })}>
            {BUILDER_EVENT_LIMITS.map((limit) => <option key={limit} value={limit}>{limit}</option>)}
          </Select>
        </Field>
      </div>
      {issues.map((issue) => <p key={issue.message} className={styles.inspectorHint} role="alert">{issue.message}</p>)}
      <div className={styles.builderSection}>
        <Switch label="Player milestones" checked={Boolean(league.milestones)} disabled={disabled || (!league.milestones && !starters.length && !canStartPosted)} onChange={(checked) => update({
          milestones: checked
            ? { label: 'Player Milestones', groups: starters.length ? starters.slice(0, BUILDER_LIMITS.groups).map((starter) => groupFromTemplate(starter)) : [groupFromPostedMarkets([], markets)] }
            : undefined,
        })} />
        {league.milestones ? <>
          <Field label="Tab label"><Input size="compact" value={league.milestones.label} maxLength={24} disabled={disabled} onChange={(event) => update({ milestones: { ...league.milestones, label: event.target.value } })} /></Field>
          {groups.map((group, index) => (
            <GroupEditor key={group.id} group={group} position={index} total={groups.length} markets={markets} starters={starters} disabled={disabled}
              onChange={(next) => setGroups(groups.map((item, at) => (at === index ? next : item)))}
              onRemove={() => setGroups(groups.filter((_, at) => at !== index))} />
          ))}
          <Button size="compact" variant="secondary" disabled={disabled || groups.length >= BUILDER_LIMITS.groups || (!unusedStarter && !canAddPosted)}
            onClick={() => setGroups([...groups, unusedStarter ? groupFromTemplate(unusedStarter) : groupFromPostedMarkets(shownColumns, markets)])}><Plus size={14} aria-hidden="true" /> Add stat</Button>
        </> : null}
        {note ? <p className={styles.inspectorHint} role="note">{note}</p> : null}
      </div>
      <GameLinesEditor league={league} choices={lines} note={linesDiscoveryNote(live, lineDiscovery)} disabled={disabled} onChange={(next) => update({ game_lines: next })} />
    </div>
  )
}

/**
 * The builder's leagues: chips customers switch between, each with its games,
 * player milestone grids and game lines from provider markets.
 */
export function ParlayBuilderLeaguesControl({ target, screen, catalog, slug, disabled, onChange }) {
  const leagues = target.leagues || []
  const [selected, setSelected] = useState(0)
  const index = Math.max(0, Math.min(selected, leagues.length - 1))
  const league = leagues[index]
  const hub = screen?.context?.kind === 'competition' ? screen.context : null
  const setLeagues = (next) => onChange('leagues', next)
  const used = new Set(leagues.map((item) => leagueKey(item.sport, item.competition).toLowerCase()))
  const choices = leagueChoices(catalog, league || {}, TEMPLATE_LEAGUES)
  const nextChoice = choices.find((choice) => !used.has(leagueKey(choice.sport, choice.competition).toLowerCase()))
  const issues = builderAuthoringIssues(target)
  const move = (offset) => {
    const next = [...leagues]
    const [item] = next.splice(index, 1)
    next.splice(index + offset, 0, item)
    setLeagues(next)
    setSelected(index + offset)
  }
  return (
    <fieldset className={styles.playerPropColumns} disabled={disabled}>
      <legend>Leagues</legend>
      <div className={styles.builderLeagueTabs}>
        {leagues.map((item, at) => (
          <button key={item.id} type="button" aria-pressed={at === index} onClick={() => setSelected(at)}>{item.label || item.competition}</button>
        ))}
        <Button size="compact" variant="secondary" disabled={disabled || Boolean(hub) || leagues.length >= BUILDER_LIMITS.leagues || !nextChoice} onClick={() => {
          setLeagues([...leagues, leagueFor(nextChoice.sport, nextChoice.competition)])
          setSelected(leagues.length)
        }}><Plus size={14} aria-hidden="true" /> Add league</Button>
      </div>
      {league ? <LeagueEditor key={league.id} league={league} position={index} total={leagues.length} choices={choices} used={used} hub={hub} catalog={catalog} slug={slug} disabled={disabled}
        issues={issues.filter((issue) => issue.league === index)}
        onChange={(next) => setLeagues(leagues.map((item, at) => (at === index ? next : item)))}
        onMove={move}
        onRemove={() => { setLeagues(leagues.filter((_, at) => at !== index)); setSelected(Math.max(0, index - 1)) }} /> : null}
    </fieldset>
  )
}
